import {
  createTourTranslationsConfig,
  type TourTranslation,
} from '@/config/objects/tour-translations';
import { BulkTranslateTourDialog } from '@/components/tours/translations/BulkTranslateTourDialog';
import { CrudListView } from '@startupquickstart/frontend';
import { Languages } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

interface TourTranslationsListProps {
  tourId: string;
  className?: string;
}

/**
 * Displays all translations for a tour using standard CrudListView. The
 * "Generate Translations" header action opens a bulk dialog that translates
 * every attraction (and the theme variant used in the tour) and generates
 * voiceovers in the background.
 */
export function TourTranslationsList({
  tourId,
  className,
}: TourTranslationsListProps) {
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const navigate = useNavigate();

  const handleGenerateTranslations = useCallback(() => {
    setBulkDialogOpen(true);
  }, []);

  const translationsConfig = useMemo(
    () =>
      createTourTranslationsConfig(tourId, {
        onGenerateTranslations: handleGenerateTranslations,
      }),
    [tourId, handleGenerateTranslations],
  );

  const handleItemClick = useCallback(
    (translation: TourTranslation) => {
      navigate(`/tours/${tourId}/translations/${translation.id}`);
    },
    [tourId, navigate],
  );

  return (
    <>
      <div className={className}>
        <CrudListView
          config={translationsConfig}
          baseUrl={`/tours/${tourId}/translations`}
          onItemClick={handleItemClick}
          getValueFromItem={(translation: TourTranslation) => translation.id}
          getLabelFromItem={(translation: TourTranslation) =>
            `${translation.language?.name || 'Unknown'} - ${translation.name}`
          }
          headerActions={[
            {
              key: 'generate',
              label: 'Generate Translations',
              icon: Languages,
              variant: 'default' as const,
              onClick: () => setBulkDialogOpen(true),
            },
          ]}
        />
      </div>

      <BulkTranslateTourDialog
        tourId={tourId}
        open={bulkDialogOpen}
        onClose={() => setBulkDialogOpen(false)}
      />
    </>
  );
}
